import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import PageTitle from '../../layouts/PageTitle';
import FormSection from '../../components/Common/Form/FormSection';
import LocalStorageType from '../../../constants/LocalStorageType';
import { getHelpTutorials } from '../../../services/HelpTutorialService';
import TutorialPlayer from './TutorialPlayer';
import './HelpCenter.css';

/**
 * Lê os tutoriais concluídos.
 *
 * O acesso ao localStorage é protegido porque em navegação privada ou com dados do site
 * bloqueados a leitura lança exceção, e nesse caso a tela precisa abrir mesmo sem histórico.
 */
const readWatched = () => {
    try {
        const stored = localStorage.getItem(LocalStorageType.HELP_WATCHED_TUTORIALS);
        const parsed = stored ? JSON.parse(stored) : [];
        return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
        return [];
    }
};

/** Grava os tutoriais concluídos, ignorando falha de armazenamento. */
const persistWatched = (ids) => {
    try {
        localStorage.setItem(LocalStorageType.HELP_WATCHED_TUTORIALS, JSON.stringify(ids));
    } catch (error) {
        // Sem espaço ou sem permissão: a marcação vale apenas para esta visita.
    }
};

/** Duração em minutos e segundos, no formato que o usuário lê no player (7:05). */
const formatDuration = (seconds) => {
    if (!seconds) return null;

    const minutes = Math.floor(seconds / 60);
    const rest = Math.round(seconds % 60);
    return `${minutes}:${String(rest).padStart(2, '0')}`;
};

/** Normaliza texto para a busca ignorar acento e caixa. */
const normalize = (text) => (text || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

/**
 * Central de Ajuda: tutoriais em vídeo do Colmeia.
 *
 * Os vídeos vêm do Bunny Stream pelo backend, agrupados pelas coleções da biblioteca. A
 * tela não decide o que existe nem em que ordem: quem publica escolhe isso no painel do
 * Bunny, e aqui só cuidamos de encontrar o vídeo (busca e assunto) e de acompanhar o que
 * já foi visto.
 */
const HelpCenter = () => {

    const { t } = useTranslation();

    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [available, setAvailable] = useState(true);
    const [sections, setSections] = useState([]);

    const [search, setSearch] = useState('');
    const [activeSection, setActiveSection] = useState('');
    const [selectedId, setSelectedId] = useState(null);
    const [watched, setWatched] = useState(readWatched);

    // Usado para trazer o player à vista quando o usuário escolhe um vídeo já com a
    // página rolada — no celular a lista é longa e o player fica fora da tela.
    const playerAnchorRef = useRef(null);

    useEffect(() => {
        let cancelled = false;

        getHelpTutorials()
            .then(({ data }) => {
                if (cancelled) return;

                const loaded = Array.isArray(data?.sections) ? data.sections : [];
                setAvailable(data?.available !== false);
                setSections(loaded);

                // Abre já com o primeiro tutorial carregado no player, sem tocar: quem chega
                // aqui perdido não precisa descobrir que o cartão é clicável para começar.
                const first = loaded.flatMap(section => section.videos || [])[0];
                if (first) setSelectedId(first.id);
            })
            .catch(() => {
                if (!cancelled) setLoadError(true);
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, []);

    const allVideos = useMemo(
        () => sections.flatMap(section => section.videos || []),
        [sections],
    );

    // A ordem da lista já vem resolvida do backend, então "próximo" é simplesmente o
    // vídeo seguinte no catálogo inteiro, atravessando o fim de um assunto para o começo
    // do outro, que é como uma trilha de treinamento é assistida.
    const selectedVideo = useMemo(
        () => allVideos.find(video => video.id === selectedId) || null,
        [allVideos, selectedId],
    );

    const nextVideo = useMemo(() => {
        if (!selectedId) return null;

        const index = allVideos.findIndex(video => video.id === selectedId);
        return index >= 0 ? allVideos[index + 1] || null : null;
    }, [allVideos, selectedId]);

    // Busca e assunto se combinam: filtrar por assunto e digitar restringe dentro dele.
    const visibleSections = useMemo(() => {
        const term = normalize(search.trim());

        return sections
            .filter(section => !activeSection || section.id === activeSection)
            .map(section => ({
                ...section,
                videos: (section.videos || []).filter(video => !term
                    || normalize(video.title).includes(term)
                    || normalize(video.description).includes(term)),
            }))
            .filter(section => section.videos.length > 0);
    }, [sections, search, activeSection]);

    const watchedCount = useMemo(
        () => allVideos.filter(video => watched.includes(video.id)).length,
        [allVideos, watched],
    );

    const progressPercent = allVideos.length
        ? Math.round((watchedCount / allVideos.length) * 100)
        : 0;

    /** Marca como concluído sem duplicar o id já registrado. */
    const markWatched = useCallback((id) => {
        setWatched((current) => {
            if (current.includes(id)) return current;

            const updated = [...current, id];
            persistWatched(updated);
            return updated;
        });
    }, []);

    /** Desmarca o tutorial, para quem quiser rever a trilha do zero. */
    const unmarkWatched = (id) => {
        setWatched((current) => {
            const updated = current.filter(item => item !== id);
            persistWatched(updated);
            return updated;
        });
    };

    const selectVideo = (id) => {
        setSelectedId(id);
        // O scroll suave só faz sentido depois de o player existir na tela.
        setTimeout(() => playerAnchorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
    };

    if (loading) {
        return (
            <>
                <PageTitle activeMenu={t('help.tutorials')} motherMenu={t('help.title')} />
                <div className="help-center__loading">
                    <Spinner animation="border" variant="primary" />
                </div>
            </>
        );
    }

    return (
        <>
            <PageTitle activeMenu={t('help.tutorials')} motherMenu={t('help.title')} />

            <Alert variant="info" className="py-2">
                <i className="fa-solid fa-circle-info me-2"></i>
                {t('help.subtitle')}
            </Alert>

            {loadError && (
                <Alert variant="warning" className="py-2">
                    <i className="fa-solid fa-triangle-exclamation me-2"></i>
                    {t('help.load_error')}
                </Alert>
            )}

            {!loadError && !available && (
                <Alert variant="warning" className="py-2">
                    <i className="fa-solid fa-circle-info me-2"></i>
                    {t('help.unavailable')}
                </Alert>
            )}

            {!loadError && available && allVideos.length === 0 && (
                <Alert variant="warning" className="py-2">
                    <i className="fa-solid fa-circle-info me-2"></i>
                    {t('help.empty')}
                </Alert>
            )}

            {allVideos.length > 0 && (
                <>
                    {/* Player em cima: a tela existe para assistir, então o vídeo escolhido não
                        pode depender de rolagem para aparecer. */}
                    <span ref={playerAnchorRef}></span>

                    {selectedVideo && (
                        <FormSection
                            className="help-center__watching"
                            title={selectedVideo.title}
                            description={selectedVideo.description || undefined}
                            icon="fa-solid fa-circle-play"
                            compact
                            actions={(
                                <div className="help-center__watching-actions">
                                    {watched.includes(selectedVideo.id) ? (
                                        <Button
                                            variant="outline-secondary"
                                            size="sm"
                                            onClick={() => unmarkWatched(selectedVideo.id)}
                                        >
                                            <i className="fa-solid fa-rotate-left me-2"></i>
                                            {t('help.mark_unwatched')}
                                        </Button>
                                    ) : (
                                        <Button
                                            variant="outline-primary"
                                            size="sm"
                                            onClick={() => markWatched(selectedVideo.id)}
                                        >
                                            <i className="fa-solid fa-check me-2"></i>
                                            {t('help.mark_watched')}
                                        </Button>
                                    )}

                                    {nextVideo && (
                                        <Button variant="primary" size="sm" onClick={() => selectVideo(nextVideo.id)}>
                                            {t('help.next')}
                                            <i className="fa-solid fa-chevron-right ms-2"></i>
                                        </Button>
                                    )}
                                </div>
                            )}
                        >
                            {/* Terminar o vídeo não emenda o seguinte: o usuário pode ter aberto
                                o tutorial no meio de um atendimento. O próximo fica no botão. */}
                            <TutorialPlayer video={selectedVideo} onWatched={markWatched} />
                        </FormSection>
                    )}

                    {/* Filtros antes dos cartões, na mesma ordem das listagens do sistema. */}
                    <FormSection className="help-center__filters" compact>
                        <div className="help-center__filters-row">
                            <div className="help-center__search">
                                <i className="fa-solid fa-magnifying-glass"></i>
                                <input
                                    type="search"
                                    className="form-control"
                                    value={search}
                                    onChange={(event) => setSearch(event.target.value)}
                                    placeholder={t('help.search_placeholder')}
                                    aria-label={t('help.search_placeholder')}
                                />
                            </div>

                            <div className="help-center__progress" aria-live="polite">
                                <span>{t('help.progress', { watched: watchedCount, total: allVideos.length })}</span>
                                <div className="help-center__progress-bar">
                                    <div style={{ width: `${progressPercent}%` }}></div>
                                </div>
                            </div>
                        </div>

                        {/* Um assunto por chip. Com uma coleção só, o filtro não ajudaria em nada
                            e por isso nem aparece. */}
                        {sections.length > 1 && (
                            <div className="help-center__chips">
                                <button
                                    type="button"
                                    className={`help-center__chip ${activeSection === '' ? 'is-active' : ''}`}
                                    onClick={() => setActiveSection('')}
                                >
                                    {t('help.all_subjects')}
                                </button>
                                {sections.map(section => (
                                    <button
                                        key={section.id || section.name}
                                        type="button"
                                        className={`help-center__chip ${activeSection === section.id ? 'is-active' : ''}`}
                                        onClick={() => setActiveSection(section.id)}
                                    >
                                        {section.name}
                                        <span className="help-center__chip-count">{(section.videos || []).length}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </FormSection>

                    {visibleSections.length === 0 && (
                        <Alert variant="warning" className="py-2">
                            <i className="fa-solid fa-circle-info me-2"></i>
                            {t('help.no_results')}
                        </Alert>
                    )}

                    {visibleSections.map(section => (
                        <FormSection
                            key={section.id || section.name}
                            className="help-center__section"
                            title={section.name}
                            compact
                        >
                            <div className="help-center__grid">
                                {section.videos.map(video => {
                                    const isWatched = watched.includes(video.id);
                                    const duration = formatDuration(video.durationSeconds);

                                    return (
                                        <button
                                            type="button"
                                            key={video.id}
                                            className={`help-center__card ${selectedId === video.id ? 'is-selected' : ''}`}
                                            onClick={() => selectVideo(video.id)}
                                        >
                                            <span className="help-center__thumb">
                                                {video.thumbnailUrl
                                                    ? <img src={video.thumbnailUrl} alt="" loading="lazy" />
                                                    : <i className="fa-solid fa-film"></i>}

                                                <span className="help-center__thumb-play" aria-hidden="true">
                                                    <i className="fa-solid fa-play"></i>
                                                </span>

                                                {duration && <span className="help-center__duration">{duration}</span>}

                                                {isWatched && (
                                                    <span className="help-center__watched-badge" title={t('help.watched')}>
                                                        <i className="fa-solid fa-check"></i>
                                                    </span>
                                                )}
                                            </span>

                                            <span className="help-center__card-text">
                                                <strong>{video.title}</strong>
                                                {video.description && <small>{video.description}</small>}
                                            </span>
                                        </button>
                                    );
                                })}
                            </div>
                        </FormSection>
                    ))}
                </>
            )}
        </>
    );
};

export default HelpCenter;
